import { format, parseISO } from "date-fns";
import { uk } from "date-fns/locale";
import { FileText } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * D-055 S4 — Report Required State (visual v0.2 екран 2).
 *
 * «Зустріч проведена · звіт очікується» — банер у working-queue та на
 * Client360 3C surface. Єдиний живий CTA — «Заповнити звіт». Доктрина:
 * проведена зустріч без звіту = незавершена робота (open work). Прострочення
 * (reportOverdueSince) → red-варіант; D-043 meeting_report_overdue вже
 * сповістив керівника (E2).
 */
export function ReportRequiredBanner({
  summary,
  accountName,
  occurredAt,
  slaDueAt,
  overdueSince,
  onFill,
  canFill,
}: {
  summary: string;
  accountName?: string | null;
  occurredAt?: string | null;
  slaDueAt?: string | null;
  overdueSince?: string | null;
  onFill: () => void;
  /** backend allowedActions.canFillReport — CTA ховається для не-виконавця. */
  canFill: boolean;
}) {
  const overdue = !!overdueSince;

  const fmt = (v: string | null | undefined, pattern: string) => {
    if (!v) return null;
    try {
      return format(parseISO(v.replace(" ", "T")), pattern, { locale: uk });
    } catch {
      return null;
    }
  };

  const occurredLabel = fmt(occurredAt, "dd.MM HH:mm");
  const slaLabel = fmt(slaDueAt, "dd.MM HH:mm");

  return (
    <div
      role="status"
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3",
        overdue
          ? "border-destructive/40 bg-destructive/5"
          : "border-warning-foreground/20 bg-warning",
      )}
    >
      <FileText
        className={cn(
          "h-6 w-6 shrink-0",
          overdue ? "text-destructive" : "text-warning-foreground",
        )}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "text-sm font-semibold",
            overdue
              ? "text-destructive"
              : "text-warning-foreground",
          )}
        >
          {overdue
            ? "Зустріч проведена · звіт прострочено"
            : "Зустріч проведена · звіт очікується"}
        </p>
        <p
          className={cn(
            "mt-0.5 truncate text-xs",
            overdue
              ? "text-destructive/90"
              : "text-warning-foreground/90",
          )}
        >
          «{summary}»
          {accountName ? ` · ${accountName}` : ""}
          {occurredLabel ? ` · проведено ${occurredLabel}` : ""}
          {overdue ? (
            <> · звіт мав бути {fmt(overdueSince, "dd.MM HH:mm")} · керівник сповіщений</>
          ) : slaLabel ? (
            <>
              {" · "}
              <b>SLA: звіт до {slaLabel} (24 робочі години)</b>
            </>
          ) : null}
        </p>
      </div>
      {canFill && (
        <button
          type="button"
          onClick={onFill}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          Заповнити звіт →
        </button>
      )}
    </div>
  );
}
